import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import './Modal.css';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ── Internal context (passes generated title id to ModalHeader) ──

const ModalCtx = createContext<{ titleId: string } | null>(null);

// ── ModalHeader ──────────────────────────────────────────────────

export interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  onClose?: () => void;
}

export function ModalHeader({ title, subtitle, onClose }: ModalHeaderProps) {
  const ctx = useContext(ModalCtx);
  return (
    <div className="modal__header">
      <div className="modal__header-text">
        <h2 className="modal__title" id={ctx?.titleId}>{title}</h2>
        {subtitle && <p className="modal__subtitle">{subtitle}</p>}
      </div>
      {onClose && (
        <button
          className="modal__close"
          onClick={onClose}
          type="button"
          aria-label="Close dialog"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}

// ── ModalBody ────────────────────────────────────────────────────

export interface ModalBodyProps {
  children: ReactNode;
  noPadding?: boolean;
}

export function ModalBody({ children, noPadding }: ModalBodyProps) {
  return (
    <div className={`modal__body${noPadding ? ' modal__body--no-padding' : ''}`}>
      {children}
    </div>
  );
}

// ── ModalFooter ──────────────────────────────────────────────────

export interface ModalFooterProps {
  onCancel?: () => void;
  cancelText?: string;
  onConfirm?: () => void;
  confirmText?: string;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  isDestructive?: boolean;
  stepCounter?: ReactNode;
}

export function ModalFooter({
  onCancel,
  cancelText = 'Cancel',
  onConfirm,
  confirmText = 'Confirm',
  confirmDisabled = false,
  confirmLoading = false,
  isDestructive = false,
  stepCounter,
}: ModalFooterProps) {
  return (
    <div className="modal__footer">
      {onCancel && (
        <button className="modal__btn modal__btn--ghost" onClick={onCancel} type="button">
          {cancelText}
        </button>
      )}
      {stepCounter != null
        ? <div className="modal__step-counter">{stepCounter}</div>
        : <div className="modal__footer-spacer" />}
      {onConfirm && (
        <button
          className={`modal__btn modal__btn--primary${isDestructive ? ' modal__btn--destructive' : ''}`}
          onClick={onConfirm}
          disabled={confirmDisabled || confirmLoading}
          type="button"
        >
          {confirmLoading && <Loader2 size={15} className="modal__btn-spinner" />}
          {confirmText}
        </button>
      )}
    </div>
  );
}

// ── StepIndicator (used by StepModal) ────────────────────────────

export interface StepIndicatorProps {
  steps: string[];
  currentStep: number;
  /** Furthest step index reached so far. Steps up to here have already been
   *  validated at least once, so they stay clickable even after going back —
   *  only steps beyond this (never visited) stay non-interactive. Defaults to
   *  currentStep, i.e. only earlier steps are clickable. */
  maxStep?: number;
  /** Jump directly to an already-reached step. */
  onStepClick?: (index: number) => void;
}

export function StepIndicator({ steps, currentStep, maxStep = currentStep, onStepClick }: StepIndicatorProps) {
  return (
    <div className="modal__steps-strip">
      <div className="modal__steps-row">
        {steps.map((label, i) => {
          const clickable = !!onStepClick && i !== currentStep && i <= maxStep;
          const content = (
            <>
              <div
                className={[
                  'modal__step-dot',
                  i < currentStep ? 'modal__step-dot--done' : '',
                  i === currentStep ? 'modal__step-dot--active' : '',
                ].filter(Boolean).join(' ')}
              >
                {i < currentStep ? <Check size={11} strokeWidth={3} /> : i + 1}
              </div>
              <span
                className={[
                  'modal__step-label',
                  i === currentStep ? 'modal__step-label--active' : '',
                  i < currentStep ? 'modal__step-label--done' : '',
                ].filter(Boolean).join(' ')}
              >
                {label}
              </span>
            </>
          );
          return (
            <React.Fragment key={`${i}-${label}`}>
              {i > 0 && (
                <div
                  className={`modal__step-connector${i <= currentStep ? ' modal__step-connector--done' : ''}`}
                />
              )}
              {clickable ? (
                <button
                  type="button"
                  className="modal__step-item modal__step-item--clickable"
                  onClick={() => onStepClick!(i)}
                  aria-label={`Go to step ${i + 1}: ${label}`}
                >
                  {content}
                </button>
              ) : (
                <div className="modal__step-item">{content}</div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────

/** search: 640px wide, at most 560px tall (the Ctrl/⌘ K panel). */
export type ModalSize = 'sm' | 'default' | 'lg' | 'xl' | 'cert' | 'search';

/** center (default; a bottom sheet on phones), or top: 15vh from the top, 16px on phones. */
export type ModalPosition = 'center' | 'top';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  confirmRequired?: boolean;
  size?: ModalSize;
  position?: ModalPosition;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  confirmRequired = false,
  size = 'default',
  position = 'center',
  children,
}: ModalProps) {
  const uid = useId();
  const titleId = `modal-title-${uid.replace(/:/g, '')}`;
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Save trigger element on open; restore focus on close
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
    } else {
      triggerRef.current?.focus();
    }
  }, [open]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Auto-focus first focusable element after entry animation
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const raf = requestAnimationFrame(() => {
      const el = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      el?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmRequired) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const nodes = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    },
    [confirmRequired, onClose],
  );

  if (!open) return null;

  return createPortal(
    <ModalCtx.Provider value={{ titleId }}>
      <div
        className={`modal__backdrop${position === 'top' ? ' modal__backdrop--top' : ''}`}
        onMouseDown={
          confirmRequired
            ? undefined
            : (e) => { if (e.target === e.currentTarget) onClose(); }
        }
        role="presentation"
      >
        <div
          ref={panelRef}
          className={`modal__panel modal__panel--${size}`}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </ModalCtx.Provider>,
    document.body,
  );
}
